import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { GOOGLE_CLIENT_ID } from "../lib/config";
import { motion, AnimatePresence } from "motion/react";
import { Eye, EyeOff, Mail, Lock, User, X, AlertCircle } from "lucide-react";

// For typescript compiler
declare global {
  interface Window {
    google?: any;
  }
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: "login" | "signup";
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialTab = "login" }) => {
  const { login, signup, googleLogin, mockGoogleLogin, authError, clearError } = useAuth();
  const [activeTab, setActiveTab] = useState<"login" | "signup">(initialTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Dev-mode mock google states
  const [showDevMock, setShowDevMock] = useState(false);
  const [mockEmail, setMockEmail] = useState("tester@wesitex.com");
  const [mockName, setMockName] = useState("Wesi Tester");

  const googleBtnRef = useRef<HTMLDivElement>(null);

  // Reset states on tab change or open
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setEmail("");
      setPassword("");
      setName("");
      setLocalError(null);
      clearError();
      setShowDevMock(false);
    }
  }, [isOpen, initialTab]);

  // Initialize Google Login if client ID is set
  useEffect(() => {
    if (isOpen && GOOGLE_CLIENT_ID) {
      const initGoogle = () => {
        if (!window.google?.accounts?.id) return;
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: async (response: any) => {
              setIsSubmitting(true);
              setLocalError(null);
              try {
                await googleLogin(response.credential);
                onClose();
              } catch (err: any) {
                setLocalError(err.message || "Google Authentication failed");
              } finally {
                setIsSubmitting(false);
              }
            },
          });

          if (googleBtnRef.current) {
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              theme: "filled_black",
              size: "large",
              shape: "pill",
              width: googleBtnRef.current.clientWidth || 320,
            });
          }
        } catch (err) {
          console.error("Error rendering Google Sign-In button", err);
        }
      };

      if (window.google?.accounts?.id) {
        initGoogle();
      } else {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => setTimeout(initGoogle, 100);
        document.body.appendChild(script);
      }
    }
  }, [isOpen, activeTab, showDevMock]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();

    if (!email || !password || (activeTab === "signup" && !name)) {
      setLocalError("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      if (activeTab === "login") {
        await login(email, password);
      } else {
        await signup(email, password, name);
      }
      onClose();
    } catch (err: any) {
      // Errors are set in the context, but we capture them here too
      setLocalError(err.message || "Authentication failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMockGoogleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();

    if (!mockEmail || !mockName) {
      setLocalError("Please fill in mock profile details.");
      return;
    }

    setIsSubmitting(true);
    try {
      await mockGoogleLogin(mockEmail, mockName);
      onClose();
    } catch (err: any) {
      setLocalError(err.message || "Mock Authentication failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = localError || authError;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-950/70 backdrop-blur-md cursor-pointer"
        />

        {/* Modal container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 15 }}
          transition={{ type: "spring", stiffness: 300, damping: 28 }}
          className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-[#07050e]/90 p-6 md:p-8 shadow-[0_0_50px_rgba(139,92,246,0.25)] text-white"
        >
          {/* Neon mesh background glow */}
          <div className="absolute -top-12 -left-12 -z-10 h-32 w-32 rounded-full bg-violet-600/30 blur-2xl" />
          <div className="absolute -bottom-12 -right-12 -z-10 h-32 w-32 rounded-full bg-sky-500/20 blur-2xl" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="font-orbitron text-2xl font-bold tracking-wider">
              {showDevMock ? "Google Auth Dev Mode" : "WESITEX PORTAL"}
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              {showDevMock ? "Simulate Google Account Sign-In" : "Access your secure cloud workspace"}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {showDevMock ? (
              // Google Auth Developer Mock Mode Form
              <motion.form
                key="dev-mock"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                onSubmit={handleMockGoogleSubmit}
                className="space-y-4"
              >
                {displayError && (
                  <div className="flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/20 p-3 rounded-lg text-sm">
                    <AlertCircle size={16} className="shrink-0" />
                    <span>{displayError}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                    Full Name (Google Profile)
                  </label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <input
                      type="text"
                      value={mockName}
                      onChange={(e) => setMockName(e.target.value)}
                      placeholder="John Doe"
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-11 pr-4 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 transition-all font-sans"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                    Email Address (Google Account)
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <input
                      type="email"
                      value={mockEmail}
                      onChange={(e) => setMockEmail(e.target.value)}
                      placeholder="john@gmail.com"
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-11 pr-4 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 transition-all font-sans"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-linear-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-xl py-3 text-sm font-semibold tracking-wider hover:shadow-[0_0_15px_rgba(139,92,246,0.4)] active:scale-98 transition-all cursor-pointer disabled:opacity-50 disabled:pointer-events-none mt-2"
                >
                  {isSubmitting ? "Authenticating..." : "Simulate Success & Login"}
                </button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => setShowDevMock(false)}
                    className="text-xs text-brand-electric hover:underline cursor-pointer"
                  >
                    ← Back to standard login
                  </button>
                </div>
              </motion.form>
            ) : (
              // Standard Email/Password and Google Form
              <motion.div
                key="standard-form"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                {/* Tabs */}
                <div className="flex border-b border-white/10 mb-6">
                  <button
                    onClick={() => {
                      setActiveTab("login");
                      setLocalError(null);
                      clearError();
                    }}
                    className={`flex-1 pb-3 text-sm font-semibold tracking-wide border-b-2 transition-all cursor-pointer ${
                      activeTab === "login"
                        ? "border-brand-purple text-white"
                        : "border-transparent text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    Log In
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab("signup");
                      setLocalError(null);
                      clearError();
                    }}
                    className={`flex-1 pb-3 text-sm font-semibold tracking-wide border-b-2 transition-all cursor-pointer ${
                      activeTab === "signup"
                        ? "border-brand-purple text-white"
                        : "border-transparent text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    Sign Up
                  </button>
                </div>

                {displayError && (
                  <div className="flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/20 p-3 rounded-lg text-sm mb-4">
                    <AlertCircle size={16} className="shrink-0" />
                    <span>{displayError}</span>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {activeTab === "signup" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="overflow-visible"
                    >
                      <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                        Full Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your name"
                          className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-11 pr-4 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 transition-all font-sans"
                          required={activeTab === "signup"}
                        />
                      </div>
                    </motion.div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-11 pr-4 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 transition-all font-sans"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5 flex justify-between">
                      <span>Password</span>
                      {activeTab === "login" && (
                        <a href="#reset" className="text-[10px] text-brand-electric hover:underline tracking-normal">
                          Forgot?
                        </a>
                      )}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-11 pr-10 text-sm focus:outline-none focus:border-brand-purple/50 focus:bg-white/10 transition-all font-sans"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white cursor-pointer"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-linear-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-xl py-3 text-sm font-semibold tracking-wider hover:shadow-[0_0_15px_rgba(139,92,246,0.4)] active:scale-98 transition-all cursor-pointer disabled:opacity-50 disabled:pointer-events-none mt-2"
                  >
                    {isSubmitting ? "Processing..." : activeTab === "login" ? "Log In" : "Register Account"}
                  </button>
                </form>

                {/* Google Sign-in Divider */}
                <div className="relative flex items-center justify-center my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <span className="relative px-3 bg-[#07050e] text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Or secure credentials via
                  </span>
                </div>

                {/* Google Sign In Container */}
                <div className="flex justify-center w-full min-h-[44px]">
                  {GOOGLE_CLIENT_ID ? (
                    <div ref={googleBtnRef} className="w-full max-w-sm flex justify-center"></div>
                  ) : (
                    // Fallback Mock Button if Client ID is not setup
                    <button
                      type="button"
                      onClick={() => setShowDevMock(true)}
                      className="w-full max-w-xs flex items-center justify-center gap-3 bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 transition-all rounded-full py-2.5 px-4 font-sans text-sm font-medium hover:shadow-lg active:scale-98 cursor-pointer"
                    >
                      <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.85z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.85c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      <span>Sign in with Google (Dev Mode)</span>
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
